import React, { useState, useRef, useEffect, ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { PermissionFlags } from '../PermissionFlags/PermissionFlags';

interface SubmenuItem {
  headline: string;
  text: ReactNode;
  href?: string;
  onClick?: () => void;
  isDisabled?: boolean;
  flags?: string[];
  icon?: ReactNode;
  styles?: React.CSSProperties;
}

interface MenuItem {
  headline: string;
  text: ReactNode;
  href?: string;
  onClick?: () => void;
  isDisabled?: boolean;
  flags?: string[];
  submenuItems?: SubmenuItem[];
  icon?: ReactNode;
  styles?: React.CSSProperties;
}

interface MenuProps {
  menuItems: MenuItem[];
  trigger: ReactNode;
  mobileTrigger?: ReactNode;
}

const FlyoutMenu: React.FC<MenuProps> = ({ menuItems, trigger, mobileTrigger }) => {
  const [openSubmenus, setOpenSubmenus] = useState<{ [key: number]: boolean }>({});
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const submenuRef = useRef<HTMLDivElement>(null);

  const isActive = (href: string) => {
    return location.pathname === href;
  };

  const handleNavigation = (path: string) => {
    navigate(path);
    setIsMenuOpen(false);
  };

  const handleSubmenuClick = (index: number) => {
    setOpenSubmenus(prevState => ({
      ...prevState,
      [index]: !prevState[index],
    }));
  };

  const handleClickOutside = (event: MouseEvent) => {
    if (submenuRef.current && !submenuRef.current.contains(event.target as Node)) {
      setOpenSubmenus({});
      setIsMenuOpen(false);
    }
  };

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <div className="relative" ref={submenuRef}>
      {/* Trigger button */}
      <div className="cursor-pointer" onClick={() => setIsMenuOpen(prev => !prev)}>
        <div className="block lg:hidden">{mobileTrigger || trigger}</div>
        <div className="hidden lg:block">{trigger}</div>
      </div>

      {/* Full-screen overlay on small screens */}
      {isMenuOpen && (
        <>
          <div className="fixed inset-0 bg-black bg-opacity-50 md:hidden" onClick={() => setIsMenuOpen(false)} />
          <nav className="fixed inset-0 flex flex-col items-start justify-start bg-white z-50 md:hidden overflow-y-auto">
            <div className="w-full flex items-center justify-between p-4 border-b border-gray-200 bg-white sticky top-0 z-20">
              <h2 className="text-lg font-semibold text-gray-800">Menu</h2>
              <button
                onClick={() => setIsMenuOpen(false)}
                className="focus:outline-none text-gray-700 hover:text-gray-900 text-2xl font-light leading-none"
              >
                ×
              </button>
            </div>
            <div className="w-full pt-2">
              {menuItems.map((item, index) => (
                <PermissionFlags requiredFlags={item.flags} key={index}>
                  <div className="w-full">
                    {item.submenuItems ? (
                      <>
                        <button
                          onClick={() => handleSubmenuClick(index)}
                          style={item.styles}
                          className={`w-full text-left px-6 py-4 transition-colors duration-300 border-b border-gray-100 flex items-center justify-between ${
                            item.isDisabled
                              ? 'cursor-not-allowed pointer-events-none text-gray-400'
                              : `hover:bg-gray-50 cursor-pointer text-gray-800 font-medium ${
                                  isActive(item.href || '') ? 'text-custom-orange bg-gray-50' : ''
                                }`
                          }`}
                        >
                          <div className="flex items-center flex-1">
                            {item.icon && <span className="mr-3">{item.icon}</span>}
                            <div>
                              <div className="font-semibold">{item.headline}</div>
                              <div className="text-sm text-gray-600">{item.text}</div>
                            </div>
                          </div>
                          <span className={`text-gray-400 transition-transform duration-200 ml-4 ${openSubmenus[index] ? 'rotate-90' : ''}`}>
                            ›
                          </span>
                        </button>
                        {openSubmenus[index] && (
                          <div className="bg-gray-50 border-b border-gray-100">
                            {item.submenuItems.map((subItem, subIndex) => (
                              <PermissionFlags requiredFlags={subItem.flags} key={subIndex}>
                                <button
                                  key={subIndex}
                                  onClick={() => {
                                    if (subItem.onClick) {
                                      subItem.onClick();
                                    } else {
                                      handleNavigation(subItem.href || '');
                                    }
                                  }}
                                  style={subItem.styles}
                                  className={`w-full text-left px-10 py-3 transition-colors duration-300 flex items-center ${
                                    subItem.isDisabled
                                      ? 'cursor-not-allowed pointer-events-none text-gray-400'
                                      : `hover:bg-gray-100 cursor-pointer text-gray-600 text-sm ${
                                          isActive(subItem.href || '') ? 'text-custom-orange font-medium bg-gray-100' : ''
                                        }`
                                  }`}
                                >
                                  {subItem.icon && <span className="mr-3 text-gray-400">{subItem.icon}</span>}
                                  <div>
                                    <div className="font-medium">{subItem.headline}</div>
                                    <div className="text-xs text-gray-500">{subItem.text}</div>
                                  </div>
                                </button>
                              </PermissionFlags>
                            ))}
                          </div>
                        )}
                      </>
                    ) : (
                      <button
                        onClick={() => {
                          if (item.onClick) {
                            item.onClick();
                          } else {
                            handleNavigation(item.href || '');
                          }
                        }}
                        style={item.styles}
                        className={`w-full text-left px-6 py-4 transition-colors duration-300 border-b border-gray-100 flex items-center ${
                          item.isDisabled
                            ? 'cursor-not-allowed pointer-events-none text-gray-400'
                            : `hover:bg-gray-50 cursor-pointer text-gray-800 font-medium ${
                                isActive(item.href || '') ? 'text-custom-orange bg-gray-50' : ''
                              }`
                        }`}
                      >
                        {item.icon && <span className="mr-3">{item.icon}</span>}
                        <div>
                          <div className="font-semibold">{item.headline}</div>
                          <div className="text-sm text-gray-600">{item.text}</div>
                        </div>
                      </button>
                    )}
                  </div>
                </PermissionFlags>
              ))}
            </div>
          </nav>
        </>
      )}

      {/* Regular dropdown for larger screens */}
      {isMenuOpen && (
        <nav className="absolute top-full w-full bg-white shadow-lg z-50 hidden md:block" style={{ left: '-50%' }}>
          <ul>
            {menuItems.map((item, index) => (
              <PermissionFlags requiredFlags={item.flags} key={index}>
                <div className="relative">
                  {item.submenuItems ? (
                    <>
                      <button
                        onClick={() => handleSubmenuClick(index)}
                        style={item.styles}
                        className={`flex items-start p-4 bg-white border-b-2 border-transparent shadow-lg transition-colors duration-300 ${
                          item.isDisabled
                            ? 'cursor-not-allowed pointer-events-none text-gray-400'
                            : `hover:text-darker-gold cursor-pointer text-gray-700 hover-underline-animation ${
                                isActive(item.href || '') ? 'text-custom-orange font-bold' : ''
                              }`
                        }`}
                      >
                        {item.icon && <span className="mr-2">{item.icon}</span>}
                        <div>
                          <div className="font-semibold">{item.headline}</div>
                          <div className="text-sm">{item.text}</div>
                        </div>
                      </button>
                      {openSubmenus[index] && (
                        <div className="absolute left-0 mt-1 bg-white border border-gray-200 shadow-lg">
                          {item.submenuItems.map((subItem, subIndex) => (
                            <PermissionFlags requiredFlags={subItem.flags} key={subIndex}>
                              <button
                                key={subIndex}
                                onClick={() =>
                                  subItem.onClick ? subItem.onClick() : handleNavigation(subItem.href || '')
                                }
                                style={subItem.styles}
                                className={`flex items-start w-full p-4 border-b-2 border-transparent transition-colors duration-300 ${
                                  subItem.isDisabled
                                    ? 'cursor-not-allowed pointer-events-none text-gray-400'
                                    : `hover:text-darker-gold cursor-pointer text-gray-700 hover-underline-animation ${
                                        isActive(subItem.href || '') ? 'text-custom-orange font-bold' : ''
                                      }`
                                }`}
                              >
                                {subItem.icon && <span className="mr-2">{subItem.icon}</span>}
                                <div>
                                  <div className="font-semibold">{subItem.headline}</div>
                                  {subItem.text}
                                </div>
                              </button>
                            </PermissionFlags>
                          ))}
                        </div>
                      )}
                    </>
                  ) : (
                    <button
                      onClick={() =>
                        item.onClick ? item.onClick() : handleNavigation(item.href || '')
                      }
                      style={item.styles}
                      className={`flex bg-white items-start shadow-lg p-4 border-b-2 border-transparent transition-colors duration-300 ${
                        item.isDisabled
                          ? 'cursor-not-allowed pointer-events-none text-gray-400'
                          : `hover:text-darker-gold cursor-pointer text-gray-700 hover-underline-animation ${
                              isActive(item.href || '') ? 'text-custom-orange font-bold' : ''
                            }`
                      }`}
                    >
                       {item.icon && <span className="mr-2">{item.icon}</span>}
                      <div>
                        <div className="font-semibold">{item.headline}</div>
                        {item.text}
                      </div>
                    </button>
                  )}
                </div>
              </PermissionFlags>
            ))}
          </ul>
        </nav>
      )}
    </div>
  );
};

export default FlyoutMenu;